import { Button } from '@/components/ui/button';
import { fmtDate } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Pencil, Ban, RotateCcw, Trash2, AlertTriangle, Clock } from 'lucide-react';

export default function ResultsTable({
  data,
  loading,
  page,
  totalPages,
  total,
  buffer,
  sortBy,
  sortOrder,
  showMode,
  onSort,
  onPageChange,
  onExportCSV,
  onExportExcel,
  onEditProduct,
  onDeactivateProduct,
  onActivateProduct,
  onDeleteProduct
}) {
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 opacity-30" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      onSort(field, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(field, 'desc');
    }
  };

  const colCount = 7; // product, barcode, supplier, total sold, last sold, suggested, actions

  return (
    <div className="border border-zinc-200 bg-white">
      {/* Table header bar */}
      <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-zinc-900" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Purchase Order
          </h3>
          {total > 0 && (
            <span className="bg-zinc-100 text-zinc-600 border border-zinc-200 px-2 py-0.5 text-xs font-bold rounded-sm">
              {total} items
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-csv-btn"
            variant="outline"
            size="sm"
            onClick={onExportCSV}
            disabled={!data?.length}
            className="rounded-none border-zinc-300 h-8 text-xs"
          >
            <Download className="h-3 w-3 mr-1.5" />
            CSV
          </Button>
          <Button
            data-testid="export-excel-btn"
            variant="outline"
            size="sm"
            onClick={onExportExcel}
            disabled={!data?.length}
            className="rounded-none border-zinc-300 h-8 text-xs"
          >
            <FileSpreadsheet className="h-3 w-3 mr-1.5" />
            Excel
          </Button>
        </div>
      </div>

      {/* Table */}
      <div data-testid="results-table" className="overflow-x-auto">
        <table className="w-full control-table">
          <thead>
            <tr>
              <th className="cursor-pointer select-none" onClick={() => handleSort('product_name')}>
                <span className="flex items-center gap-1.5">Product Name <SortIcon field="product_name" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('barcode')}>
                <span className="flex items-center gap-1.5">Barcode <SortIcon field="barcode" /></span>
              </th>
              <th className="cursor-pointer select-none" onClick={() => handleSort('supplier')}>
                <span className="flex items-center gap-1.5">Supplier <SortIcon field="supplier" /></span>
              </th>
              <th className="cursor-pointer select-none text-right" onClick={() => handleSort('total_sold')}>
                <span className="flex items-center justify-end gap-1.5">Total Sold <SortIcon field="total_sold" /></span>
              </th>
              <th className="cursor-pointer select-none text-right" onClick={() => handleSort('last_sold_date')}>
                <span className="flex items-center justify-end gap-1.5">Last Sold <SortIcon field="last_sold_date" /></span>
              </th>
              <th className="text-right">
                <span className="flex items-center justify-end gap-1">
                  Suggested Order
                  <span className="text-emerald-600 text-[10px] font-normal normal-case tracking-normal">+{buffer}%</span>
                </span>
              </th>
              <th className="text-center w-[60px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-6 w-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-zinc-400">Loading data...</span>
                  </div>
                </td>
              </tr>
            ) : !data?.length ? (
              <tr>
                <td colSpan={colCount} className="text-center py-16">
                  <div className="flex flex-col items-center gap-4">
                    <img
                      src="https://static.prod-images.emergentagent.com/jobs/bb0b9263-a37a-4e9f-8280-005b26def085/images/46f04c7b851d01a44b08bfcc2bb484214b4fddc22c087c48dccc6b648e2466d7.png"
                      alt="No data"
                      className="h-28 w-28 opacity-80"
                    />
                    <div>
                      <p className="text-sm font-medium text-zinc-600">No data available</p>
                      <p className="text-xs text-zinc-400 mt-1">Upload a sales file to get started</p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, idx) => {
                const isInactive = item.status === 'inactive';
                const hasNoSales = item.indicators?.includes('no_sales');
                const isSlow = item.indicators?.includes('slow_product');
                const isNew = item.indicators?.includes('new_product');
                const isDead = item.indicators?.includes('dead_stock');
                const rowClass = isInactive ? 'opacity-50' : '';

                return (
                  <tr
                    key={`${item.barcode}-${idx}`}
                    className={`animate-fade-in ${rowClass}`}
                    style={{ animationDelay: `${idx * 15}ms` }}
                  >
                    {/* Product Name + MRP + indicators */}
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${isInactive ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>
                          {item.product_name}
                        </span>
                        {item.mrp > 0 && (
                          <span className="font-mono text-[11px] text-zinc-400 bg-zinc-100 border border-zinc-200 px-1.5 py-0 shrink-0">
                            MRP {item.mrp}
                          </span>
                        )}
                        {isNew && (
                          <span data-testid={`badge-new-${item.barcode}`} className="bg-sky-100 text-sky-700 border border-sky-200 text-[9px] font-bold px-1 py-0 uppercase tracking-wider shrink-0">New</span>
                        )}
                        {isInactive && (
                          <span className="bg-zinc-200 text-zinc-500 text-[9px] font-bold px-1 py-0 uppercase tracking-wider shrink-0">Inactive</span>
                        )}
                      </div>
                      {/* Inline warnings */}
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {hasNoSales && (
                          <span data-testid={`warning-no-sales-${item.barcode}`} className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0 font-medium">
                            <AlertTriangle className="h-2.5 w-2.5" /> No sales in selected period
                          </span>
                        )}
                        {isDead && (
                          <span data-testid={`warning-dead-${item.barcode}`} className="flex items-center gap-1 text-[10px] text-red-700 bg-red-50 border border-red-300 px-1.5 py-0 font-semibold" title="No sales for over 90 days">
                            <Clock className="h-2.5 w-2.5" /> Dead stock
                          </span>
                        )}
                        {isSlow && !hasNoSales && !isDead && (
                          <span data-testid={`warning-slow-${item.barcode}`} className="flex items-center gap-1 text-[10px] text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0 font-medium">
                            <Clock className="h-2.5 w-2.5" /> Slow/Dead product
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="num-cell text-left text-zinc-500">{item.barcode || '-'}</td>
                    <td className="text-zinc-600">{item.supplier}</td>
                    <td className={`num-cell ${hasNoSales ? 'text-amber-600' : 'text-zinc-900'}`}>
                      {item.total_sold === 0 ? '0' : item.total_sold.toLocaleString()}
                    </td>
                    <td className="num-cell text-zinc-500 text-right">{fmtDate(item.last_sold_date) || '-'}</td>
                    <td className={`num-cell font-semibold ${item.suggested_order > 0 ? 'text-emerald-700' : 'text-zinc-300'}`}>
                      {item.suggested_order > 0 ? item.suggested_order.toLocaleString() : '-'}
                    </td>
                    <td className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            data-testid={`actions-btn-${item.barcode}`}
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-none"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-none w-44">
                          <DropdownMenuItem
                            data-testid={`edit-btn-${item.barcode}`}
                            onClick={() => onEditProduct(item)}
                            className="rounded-none text-xs cursor-pointer"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-2" /> Edit Product
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {item.status === 'active' ? (
                            <DropdownMenuItem
                              data-testid={`deactivate-btn-${item.barcode}`}
                              onClick={() => onDeactivateProduct(item)}
                              className="rounded-none text-xs cursor-pointer text-amber-600"
                            >
                              <Ban className="h-3.5 w-3.5 mr-2" /> Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              data-testid={`activate-btn-${item.barcode}`}
                              onClick={() => onActivateProduct(item)}
                              className="rounded-none text-xs cursor-pointer text-emerald-600"
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reactivate
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            data-testid={`delete-btn-${item.barcode}`}
                            onClick={() => onDeleteProduct(item)}
                            className="rounded-none text-xs cursor-pointer text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-zinc-200 px-4 py-3">
          <span className="text-xs text-zinc-500">
            Page <span className="font-mono font-semibold">{page}</span> of <span className="font-mono font-semibold">{totalPages}</span>
          </span>
          <div className="flex items-center gap-1">
            <Button
              data-testid="pagination-prev"
              variant="outline"
              size="icon"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="h-8 w-8 rounded-none border-zinc-300"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              data-testid="pagination-next"
              variant="outline"
              size="icon"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="h-8 w-8 rounded-none border-zinc-300"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
